<script lang="ts">
export default {
  name: 'PopUp'
};
</script>

<script lang="ts" setup>
import ButtonIcon from '#components/elements/button/buttonIcon/ButtonIcon.vue';
import { DialogPanel, DialogTitle, Dialog as DialogUi, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { computed } from 'vue';

const {
  open = false,
  titleSize = 'large',
  verticalPosition = 'top',
  titleCentered = false
} = defineProps<{
  open?: boolean;
  title: string;
  titleSize?: 'medium' | 'large';
  titleCentered?: boolean;
  verticalPosition?: 'top' | 'center';
}>();
const emit = defineEmits<{
  onClose: [value?: boolean];
}>();

const titleClasses = computed(() => ({
  'text-3xl': titleSize === 'large',
  'text-2xl': titleSize === 'medium',
  'text-center mt-10': titleCentered,
  'mb-6': titleSize === 'large',
  'mb-4': titleSize === 'medium'
}));
const containerClasses = computed(() => ({
  'items-start': verticalPosition === 'top',
  'items-center': verticalPosition === 'center'
}));
</script>

<template>
  <Teleport to="#modals">
    <TransitionRoot as="template" :show="open">
      <DialogUi as="div" class="relative z-50" :open="open" @close="emit('onClose')">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/25 transition-opacity" aria-hidden="true" />
        </TransitionChild>

        <div class="fixed inset-0">
          <div class="flex justify-center h-full" :class="containerClasses">
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                class="transform rounded-lg overflow-hidden p-6 w-full bg-white shadow-default transition-all sm:max-w-2xl my-8 relative"
              >
                <DialogTitle as="h3" class="text-primary font-bold" :class="titleClasses">
                  {{ title }}
                </DialogTitle>
                <ButtonIcon
                  data-testid="close-popin-button"
                  class="absolute right-4 top-4"
                  icon="x"
                  background="transparent"
                  @click="emit('onClose', false)"
                />
                <div class="h-full max-h-popupContent pb-4">
                  <slot />
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </DialogUi>
    </TransitionRoot>
  </Teleport>
</template>
